//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import { Action, createReducer, on } from '@ngrx/store';
import { advisoryDeletedFromHub, advisoryReceivedFromHub, advisoryUpdatedFromHub, alertDeletedFromHub, alertReceivedFromHub, alertUpdatedFromHub, cityReportDeletedFromHub, cityReportReceivedFromHub, clearLocalRiskLevelsResult, clearTravelSearchResult, clearTripReadyResult, countryReportDeletedFromHub, countryReportReceivedFromHub, countryRiskLevelDeletedFromHub, countryRiskLevelReceivedFromHub, executeLocalRiskLevels, executeLocalRiskLevelsFailure, executeLocalRiskLevelsSuccess, executeTravelSearch, executeTravelSearchFailure, executeTravelSearchSuccess, executeTripReady, executeTripReadyFailure, executeTripReadySuccess, fetchAdvisories, fetchAdvisoriesFailure, fetchAdvisoriesSuccess, fetchAdvisoryById, fetchAdvisoryByIdFailure, fetchAdvisoryByIdSuccess, fetchAlerts, fetchAlertsFailure, fetchAlertsSuccess, fetchAllCityReports, fetchAllCityReportsFailure, fetchAllCityReportsSuccess, fetchCityReport, fetchCityReportFailure, fetchCityReportSuccess, fetchCountryReport, fetchCountryReportFailure, fetchCountryReportSuccess, fetchCountryRiskLevels, fetchCountryRiskLevelsFailure, fetchCountryRiskLevelsSuccess, fetchPtaForCountry, fetchPtaForCountryFailure, fetchPtaForCountrySuccess, fetchStaForCountry, fetchStaForCountryFailure, fetchStaForCountrySuccess, fetchSummaryForCountry, fetchSummaryForCountryFailure, fetchSummaryForCountrySuccess, ptaDeletedFromHub, ptaReceivedFromHub, staDeletedFromHub, staReceivedFromHub, summaryDeletedFromHub, summaryReceivedFromHub } from './risk_intelligence_action';
import { LocalRiskLevelResponseDTO } from 'src/app/core/models/system/riskline/riskline-local-risk-level.dto';
import { RisklineAdvisoryModel, RisklineAlertModel, RisklineCityReportModel, RisklineCountryReportModel, RisklineCountryRiskLevelModel, RisklinePtaModel, RisklineStaModel, RisklineSummaryModel, RisklineTravelSearchModel, RisklineTripReadyModel } from './risk_intelligence_model';

export interface RiskIntelligenceState {
  advisories            : RisklineAdvisoryModel[];
  alerts                : RisklineAlertModel[];
  cityReports           : { [iata: string]: RisklineCityReportModel };
  countryReports        : { [iso: string]: RisklineCountryReportModel };
  countryRiskLevels     : RisklineCountryRiskLevelModel[];
  error                 : any;
  lastLocalRiskLevels   : LocalRiskLevelResponseDTO | null;
  lastTravelSearchResult: RisklineTravelSearchModel | null;
  lastTripReadyResult   : RisklineTripReadyModel    | null;
  loading               : boolean;
  ptas                  : { [iso: string]: RisklinePtaModel };
  stas                  : { [iso: string]: RisklineStaModel };
  summaries             : { [iso: string]: RisklineSummaryModel };
}

export const initialState: RiskIntelligenceState = {
  advisories            : [],
  alerts                : [],
  cityReports           : {},
  countryReports        : {},
  countryRiskLevels     : [],
  error                 : null,
  lastLocalRiskLevels   : null,
  lastTravelSearchResult: null,
  lastTripReadyResult   : null,
  loading               : false,
  ptas                  : {},
  stas                  : {},
  summaries             : {}
};

export const RiskIntelligenceReducer = createReducer(
  initialState,

  //#region Advisories

  on(fetchAdvisories       , (state                ) => ({ ...state, loading: true, error: null })),
  on(fetchAdvisoriesSuccess, (state, { advisories }) => ({ ...state, advisories, loading: false })),
  on(fetchAdvisoriesFailure, (state, { error      }) => ({ ...state, error, loading: false })),

  on(fetchAdvisoryById       , (state              ) => ({ ...state, loading: true, error: null })),
  on(fetchAdvisoryByIdSuccess, (state, { advisory }) => {
    const exists=state.advisories.some(a => a.id==advisory.id);
    return {
      ...state,
      advisories: exists
        ? state.advisories.map(a => a.id==advisory.id ? { ...a, ...advisory } : a)
        : [advisory, ...state.advisories],
      loading   : false
    };
  }),
  on(fetchAdvisoryByIdFailure, (state, { error })  => ({ ...state, error, loading: false })),

  //CRN_20260511 - Hub payload is flat (no `countries[]`); strip undefined before merging to preserve
  //             - the array populated by the REST list fetch.
  on(advisoryReceivedFromHub, (state, { advisory }) => {
    const exists=state.advisories.some(a => a.id==advisory.id);
    const patch=stripUndefined(advisory);

    return exists
      ? { ...state, advisories: state.advisories.map(a => a.id==advisory.id ? { ...a, ...patch } : a) }
      : { ...state, advisories: [advisory, ...state.advisories] };
  }),
  on(advisoryUpdatedFromHub, (state, { advisory }) => {
    const patch=stripUndefined(advisory);
    return { ...state, advisories: state.advisories.map(a => a.id==advisory.id ? { ...a, ...patch } : a) };
  }),
  on(advisoryDeletedFromHub, (state, { id }) => ({
    ...state,
    advisories: state.advisories.filter(a => a.id!=id)
  })),

  //#endregion

  //#region Alerts

  on(fetchAlerts       , (state            ) => ({ ...state, loading: true, error: null })),
  on(fetchAlertsSuccess, (state, { alerts }) => ({ ...state, alerts, loading: false })),
  on(fetchAlertsFailure, (state, { error  }) => ({ ...state, error, loading: false })),

  on(alertReceivedFromHub, (state, { alert }) => {
    const exists=state.alerts.some(a => a.id==alert.id);
    const patch=stripUndefined(alert);

    return exists
      ? { ...state, alerts: state.alerts.map(a => a.id==alert.id ? { ...a, ...patch } : a) }
      : { ...state, alerts: [alert, ...state.alerts] };
  }),
  on(alertUpdatedFromHub, (state, { alert }) => {
    const patch=stripUndefined(alert);
    return { ...state, alerts: state.alerts.map(a => a.id==alert.id ? { ...a, ...patch } : a) };
  }),
  on(alertDeletedFromHub, (state, { id }) => ({
    ...state,
    alerts: state.alerts.filter(a => a.id!=id)
  })),

  //#endregion

  //#region Country Risk Levels

  on(fetchCountryRiskLevels       , (state                ) => ({ ...state, loading: true, error: null })),
  on(fetchCountryRiskLevelsSuccess, (state, { riskLevels }) => ({ ...state, countryRiskLevels: riskLevels, loading: false })),
  on(fetchCountryRiskLevelsFailure, (state, { error      }) => ({ ...state, error, loading: false })),

  on(countryRiskLevelReceivedFromHub, (state, { riskLevel }) => {
    const exists=state.countryRiskLevels.some(r => r.countryIsoCode==riskLevel.countryIsoCode);

    return exists
      ? { ...state, countryRiskLevels: state.countryRiskLevels.map(r => r.countryIsoCode==riskLevel.countryIsoCode ? { ...r, ...riskLevel } : r) }
      : { ...state, countryRiskLevels: [...state.countryRiskLevels, riskLevel] };
  }),
  on(countryRiskLevelDeletedFromHub, (state, { countryIsoCode }) => ({
    ...state,
    countryRiskLevels: state.countryRiskLevels.filter(r => r.countryIsoCode!=countryIsoCode)
  })),

  //#endregion

  //#region Summaries / PTAs / Country Reports / City Reports / STAs (keyed by ISO/IATA)

  on(fetchSummaryForCountry       , (state)              => ({ ...state, loading: true, error: null })),
  on(fetchSummaryForCountrySuccess, (state, { summary }) => ({
    ...state,
    summaries: { ...state.summaries, [summary.countryIsoCode ?? '']: summary },
    loading  : false
  })),
  on(fetchSummaryForCountryFailure, (state, { error })   => ({ ...state, error, loading: false })),

  on(fetchPtaForCountry       , (state)         => ({ ...state, loading: true, error: null })),
  on(fetchPtaForCountrySuccess, (state, { pta })=> ({
    ...state,
    ptas   : { ...state.ptas, [pta.countryIsoCode ?? '']: pta },
    loading: false
  })),
  on(fetchPtaForCountryFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(fetchCountryReport       , (state)             => ({ ...state, loading: true, error: null })),
  on(fetchCountryReportSuccess, (state, { report }) => ({
    ...state,
    countryReports: { ...state.countryReports, [report.countryIsoCode ?? '']: report },
    loading       : false
  })),
  on(fetchCountryReportFailure, (state, { error })  => ({ ...state, error, loading: false })),

  on(fetchCityReport       , (state)             => ({ ...state, loading: true, error: null })),
  on(fetchCityReportSuccess, (state, { report }) => ({
    ...state,
    cityReports: { ...state.cityReports, [report.iataCode ?? '']: report },
    loading    : false
  })),
  on(fetchCityReportFailure, (state, { error })  => ({ ...state, error, loading: false })),

  on(fetchAllCityReports       , (state)              => ({ ...state, loading: true, error: null })),
  on(fetchAllCityReportsSuccess, (state, { reports })=> ({
    ...state,
    cityReports: reports.reduce((acc, r) => ({ ...acc, [r.iataCode ?? '']: r }), state.cityReports),
    loading    : false
  })),
  on(fetchAllCityReportsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(fetchStaForCountry       , (state)         => ({ ...state, loading: true, error: null })),
  on(fetchStaForCountrySuccess, (state, { sta })=> ({
    ...state,
    stas   : { ...state.stas, [sta.countryIsoCode ?? '']: sta },
    loading: false
  })),
  on(fetchStaForCountryFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(summaryReceivedFromHub, (state, { summary }) => ({
    ...state,
    summaries: { ...state.summaries, [summary.countryIsoCode ?? '']: summary }
  })),
  on(summaryDeletedFromHub, (state, { countryIsoCode }) => {
    const next={ ...state.summaries };
    delete next[countryIsoCode];
    return { ...state, summaries: next };
  }),

  on(ptaReceivedFromHub, (state, { pta }) => ({
    ...state,
    ptas: { ...state.ptas, [pta.countryIsoCode ?? '']: pta }
  })),
  on(ptaDeletedFromHub, (state, { countryIsoCode }) => {
    const next={ ...state.ptas };
    delete next[countryIsoCode];
    return { ...state, ptas: next };
  }),

  on(countryReportReceivedFromHub, (state, { report }) => ({
    ...state,
    countryReports: { ...state.countryReports, [report.countryIsoCode ?? '']: report }
  })),
  on(countryReportDeletedFromHub, (state, { countryIsoCode }) => {
    const next={ ...state.countryReports };
    delete next[countryIsoCode];
    return { ...state, countryReports: next };
  }),

  on(cityReportReceivedFromHub, (state, { report }) => ({
    ...state,
    cityReports: { ...state.cityReports, [report.iataCode ?? '']: report }
  })),
  on(cityReportDeletedFromHub, (state, { iataCode }) => {
    const next={ ...state.cityReports };
    delete next[iataCode];
    return { ...state, cityReports: next };
  }),

  on(staReceivedFromHub, (state, { sta }) => ({
    ...state,
    stas: { ...state.stas, [sta.countryIsoCode ?? '']: sta }
  })),
  on(staDeletedFromHub, (state, { isoCode }) => {
    const next={ ...state.stas };
    delete next[isoCode];
    return { ...state, stas: next };
  }),

  //#endregion

  //#region Travel Search / TripReady / Local Risk Levels (transient)

  on(executeTravelSearch       , (state)             => ({ ...state, loading: true, error: null })),
  on(executeTravelSearchSuccess, (state, { result })=> ({ ...state, lastTravelSearchResult: result, loading: false })),
  on(executeTravelSearchFailure, (state, { error }) => ({ ...state, error, loading: false })),
  on(clearTravelSearchResult   , (state)             => ({ ...state, lastTravelSearchResult: null })),

  on(executeTripReady       , (state)             => ({ ...state, loading: true, error: null })),
  on(executeTripReadySuccess, (state, { result })=> ({ ...state, lastTripReadyResult: result, loading: false })),
  on(executeTripReadyFailure, (state, { error }) => ({ ...state, error, loading: false })),
  on(clearTripReadyResult   , (state)             => ({ ...state, lastTripReadyResult: null })),

  on(executeLocalRiskLevels       , (state)             => ({ ...state, loading: true, error: null })),
  on(executeLocalRiskLevelsSuccess, (state, { result })=> ({ ...state, lastLocalRiskLevels: result, loading: false })),
  on(executeLocalRiskLevelsFailure, (state, { error }) => ({ ...state, error, loading: false })),
  on(clearLocalRiskLevelsResult   , (state)             => ({ ...state, lastLocalRiskLevels: null }))

  //#endregion
);

export function reducer(state: RiskIntelligenceState | undefined, action: Action) {
  return RiskIntelligenceReducer(state, action);
}

function stripUndefined<T extends object>(input: T): Partial<T> {
  const out: any={};

  for (const key in input) {
    if ((input as any)[key]!==undefined)
      out[key]=(input as any)[key];
  }

  return <Partial<T>>out;
}
